import { type ConnectedApp, connectedAppSchema } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppWindow, FilePen } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { InlineError, Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "../i18n/format.ts";

/**
 * Connected apps (openspec change native-device-and-cimd, D4): the native and MCP clients a
 * user authorized, each with its domain, access, when it connected and last refreshed, and a
 * Revoke that ends its refresh tokens at once. The same list serves the account page (the
 * user's own) and the backoffice user view (an admin's; hidden under a support grant).
 */
export function ConnectedApps(props: {
  queryKey: readonly unknown[];
  load: () => Promise<unknown>;
  revoke: (grantId: string) => Promise<void>;
  readOnly?: boolean;
  /** The empty-list sentence: the user's own, or the admin's view of someone. */
  empty: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const apps = useQuery({
    queryKey: props.queryKey,
    queryFn: async () => z.array(connectedAppSchema).parse(await props.load()),
  });
  const revoke = useMutation({
    mutationFn: (app: ConnectedApp) => props.revoke(app.grantId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: props.queryKey }),
  });

  if (apps.isPending) {
    return <Skeleton className="h-16 w-full" />;
  }
  if (apps.isError) {
    return <Notice tone="danger">{apps.error.message || t("errors.network")}</Notice>;
  }
  // The status line stays when the last app goes, so its revocation is still announced.
  const status = (
    <p role="status" className="text-sm text-success-fg">
      {revoke.isSuccess ? t("connectedApps.revoked") : null}
    </p>
  );
  if (apps.data.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-fg-secondary">{props.empty}</p>
        {status}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col divide-y divide-line rounded-lg border border-line bg-surface-raised">
        {apps.data.map((app) => {
          const name = app.client.name ?? app.client.host ?? app.client.id;
          return (
            <li key={app.grantId} className="flex flex-wrap items-center gap-3 px-3 py-3">
              <AppWindow aria-hidden="true" className="size-4 text-fg-muted" strokeWidth={1.75} />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-sm font-medium text-fg">{name}</span>
                <span className="break-all text-xs text-fg-muted">
                  {app.client.host
                    ? t("connect.hostHint", { host: app.client.host })
                    : t("connect.firstParty")}
                </span>
                <span className="text-xs tabular-nums text-fg-muted">
                  {[
                    app.resources.map((r) => t(`connect.resources.${r}`)).join(", "),
                    t("connectedApps.connected", { date: formatDateTime(app.createdAt) }),
                    app.lastRefreshedAt
                      ? t("connectedApps.lastUsed", { date: formatDateTime(app.lastRefreshedAt) })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                {app.scopes?.includes("write") ? (
                  <span className="flex items-center gap-1 text-xs text-fg-secondary">
                    <FilePen aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
                    {t("connectedApps.canWrite")}
                  </span>
                ) : null}
              </div>
              {props.readOnly ? null : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={revoke.isPending}
                  aria-label={t("connectedApps.revokeNamed", { client: name })}
                  onClick={() => revoke.mutate(app)}
                >
                  {t("connectedApps.revoke")}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {revoke.error ? (
        <InlineError>{revoke.error.message || t("errors.network")}</InlineError>
      ) : null}
      {status}
    </div>
  );
}
